import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useForm } from "react-final-form";
import { FormInput, FormToggle, FormSelect, FormListControl } from "ui-kit/SmartForm";
import IntlStore from "stories/IntlStore";
import { ArtistItem } from "components/NewRelease/ReleaseInfo/ArtistItem";
import { ContributorItem } from "./ContributorItem";
import { ToggleButtonChangeEvent } from "primereact/togglebutton";
import { FormValues } from "../types";
import { useArtists } from "components/NewRelease/hooks/useArtists";
import { useContributors } from "components/NewRelease/hooks/useContributors";
import { useGenres } from "components/NewRelease/hooks/useGenres";
import { AddContributorModal } from "./AddContributorModal";
import { AddArtistModal } from "./AddArtistModal";
import { useLanguages } from "components/NewRelease/hooks/useLanguages";
import { FormDateOnlyYear } from "ui-kit/SmartForm/FormDateOnlyYear";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { FormMultiSelectGaner } from "ui-kit/SmartForm/FormMultiSelectganer";
import { useParams } from "react-router";
import { useTracks } from "components/NewRelease/hooks/useTracks";

interface FormContentProps {
  values: FormValues;
  release?: FormValues | null;
  caraoke: boolean;
  setCaraoke: (caraoke: boolean) => void;
  trackId: String
  tracks: any
}

interface Language {
  id: number;
  language: string;
  code: string;
}



export const FormContent: React.FC<FormContentProps> = props => {
  const { values, release, caraoke, setCaraoke, trackId, } = props;
  const { id } = useParams();
  const { change } = useForm<FormValues>();
  const [isOpenArtistsModal, setIsOpenArtistsModal] = useState(false);
  const [isOpenContributorsModal, setIsOpenContributorsModal] = useState(false);
  const { artists, createArtist, creating: artistCreating } = useArtists();
  const { contributors, roles, instruments, createContributor, creating: contributorCreating } = useContributors();
  const [selectedArtist, setSelectedArtist] = useState<string>('');
  const [recordingYearDate, setRecordingYearDate] = useState<Number | string>('Select Year');
  const { reorderTracksArtists, upDateRole, } = useTracks();
  const { genres } = useGenres();
  const { languages } = useLanguages() as { languages: Language[] };

  const primaryGenreInitial = useMemo(() => {
    return values?.genres?.find(val => val.is_primary)?.id;
  }, [values?.genres]);

  const additionalGenresInitial = useMemo(
    () => values?.genres?.filter(val => !val.is_primary).map(val => val.id) || [],
    [values?.genres]
  );

  const stablePrimaryGenreInitial = useMemo(
    () => (primaryGenreInitial ? [Number(primaryGenreInitial)] : []),
    [primaryGenreInitial]
  );

  const stableAdditionalGenresInitial = useMemo(
    () => (additionalGenresInitial?.map(Number).filter(Boolean) || []),
    [additionalGenresInitial]
  );

  const reorder = (list: any[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  useEffect(() => {
    if (values.recording_year) {
      setRecordingYearDate(values?.recording_year as unknown as Number);
    }
  }, []);

  const [artistData, setArtistData] = useState<typeof values.artists>([]);

  useEffect(() => {
    // if (release?.release_type === "SINGLE" && release?.artists?.length && tracks.length < 1) {
    //   setArtistData([...(values?.artists || []), ...release.artists.filter(a => a?.id)]);
    // } else {
    setArtistData(values?.artists?.filter(a => a?.id) || []);
    // }
  }, [values?.artists, release]);

  const primaryArtistId = useMemo(() => {
    const list = Array.isArray(values?.artists) ? values.artists : [];
    if (!list.length) return null;

    // Only treat explicit MAIN / is_primary / checked as "primary" where possible.
    const byRole = list.find((a: any) =>
      String(a?.role ?? a?.pivot?.role ?? "").toUpperCase() === "MAIN"
    )?.id;
    if (byRole != null) return byRole;

    const byIsPrimary = list.find(
      (a: any) =>
        a?.is_primary === true ||
        a?.is_primary === 1 ||
        a?.is_primary === "1"
    )?.id;
    if (byIsPrimary != null) return byIsPrimary;

    const byChecked = list.find((a: any) => a?.checked === true)?.id;
    if (byChecked != null) return byChecked;

    // Fallback: if backend didn't mark anyone explicitly, treat the first artist as primary.
    const firstId = list.find((a: any) => a?.id != null)?.id;
    return firstId != null ? firstId : null;
  }, [values?.artists]);

  useEffect(() => {
    if (!values.artists || !values.artists.length) return;
    if (!primaryArtistId) return;

    // Normalize roles so there is exactly ONE MAIN (primaryArtistId) and others are FEAT.
    const normalized = values.artists.map((a: any) =>
      String(a?.id) === String(primaryArtistId) ? { ...a, role: "MAIN" } : { ...a, role: "FEAT" }
    );

    const alreadyNormalized = values.artists.every((a: any) => {
      const shouldBeMain = String(a?.id) === String(primaryArtistId);
      return shouldBeMain ? a?.role === "MAIN" : a?.role !== "MAIN";
    });

    if (alreadyNormalized) return;
    change("artists", normalized);
  }, [values.artists, primaryArtistId, change]);

  const isHorizontal = true;
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenMain, setIsOpenMain] = useState(false);

  const form = useForm();
  const langNormalizedForTrackRef = useRef<string>("");

  useEffect(() => {
    if (!Array.isArray(languages) || languages.length === 0) return;
    const key = String(trackId ?? "");
    if (!key) return;

    // Ensure we only normalize once per track selection (prevents "fighting" with reinit)
    const raw = (values as any)?.metadata_language ?? (values as any)?.language_id ?? "";
    if (raw == null || raw === "") return;

    const normalizedKey = `${key}:${String(raw)}`;
    if (langNormalizedForTrackRef.current === normalizedKey) return;

    const rawStr = String(raw).trim();
    const byCode = languages.find((l: any) => String(l?.code).toLowerCase() === rawStr.toLowerCase());
    if (byCode) {
      // normalize casing to the canonical code
      if (String(values?.metadata_language) !== String(byCode.code)) {
        form.change("metadata_language", String(byCode.code));
      }
      langNormalizedForTrackRef.current = normalizedKey;
      return;
    }

    // numeric id -> convert to code so dropdown can display it
    const byId = languages.find((l: any) => String(l?.id) === rawStr);
    if (byId?.code) {
      form.change("metadata_language", String(byId.code));
    }
    langNormalizedForTrackRef.current = normalizedKey;
  }, [trackId, values?.metadata_language, (values as any)?.language_id, languages, form]);

  // Keep genre fields in sync when switching tracks:
  // backend often returns `genres` array, while the form fields are `main_genre` (number[]) and `genre_ids` (number[])
  useEffect(() => {
    if (!Array.isArray(values?.genres)) return;
    const primary = values.genres.find((g: any) => g?.is_primary)?.id;
    const additional = values.genres.filter((g: any) => !g?.is_primary).map((g: any) => g?.id);

    if (primary != null) {
      const nextMain = [Number(primary)].filter((n) => Number.isFinite(n));
      const currentMain = Array.isArray((values as any)?.main_genre) ? (values as any).main_genre : [];
      if (JSON.stringify(currentMain) !== JSON.stringify(nextMain)) {
        form.change("main_genre", nextMain);
      }
    }
    if (Array.isArray(additional)) {
      const nextAdditional = additional.map((x: any) => Number(x)).filter((n: any) => Number.isFinite(n));
      const currentAdditional = Array.isArray((values as any)?.genre_ids) ? (values as any).genre_ids : [];
      if (JSON.stringify(currentAdditional) !== JSON.stringify(nextAdditional)) {
        form.change("genre_ids", nextAdditional);
      }
    }
  }, [values?.id, values?.genres, form]);



  return (
    <Container onClick={() => {
      setIsOpen(false); setIsOpenMain(false);
    }}>
      <InfoInputsContainer>
        <FormListControl
          id="atrists"
          name="artists"
          label={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.Artist",
            defaultMessage: "Artists",
          })}
          noItemsLabel={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.Artist.NoArtists",
            defaultMessage: "No added artists"
          })}
          addItemLabel={IntlStore.intl.formatMessage({
            id: "NewRelease.ReleaseInfo.Artist.AddArtists",
            defaultMessage: "Add artists"
          })}

          onAddItem={() => setIsOpenArtistsModal(true)}
          tooltip="Artist Name Rules
To ensure your release appears correctly on DSPs and in search:
✅ Enter one name per field and only artists you have a confirmed connection with.
✅ Use “Various Artists” only if your release has more than 4 artists.
🚫 Don’t use generic terms (e.g., Sex, Yoga, Workout, Meditation, Car Music, Christmas, Top Hits, Hip Hop, Instrumental, Orchestra, Singer) or descriptors like guitarist, saxophone, V/A, ft., feat.
🚫 Don’t imitate famous artists or brands, or use book titles, characters, movies, or misleading keywords.
🚫 No offensive, hateful, or discriminatory content.
"
          required
          totalItems={artistData?.length}
        // isDisabled={release?.release_type === "SINGLE" && tracks?.length < 1}
        >
          {values.artists?.length ? <DragDropContext onDragEnd={async (result: DropResult) => {
            if (!result.destination) return;

            const newArtists = reorder(
              values.artists || [],
              result.source.index,
              result.destination.index
            );

            const prevArtists = values.artists || [];
            change("artists", newArtists);
            try {
              const orderedIds = newArtists.map((a: { id: any; }) => Number(a?.id)).filter(Boolean);

              await reorderTracksArtists(Number(values?.id), { order: orderedIds });

            } catch (err) {
              change("artists", prevArtists);
            }
          }}>
            <Droppable
              droppableId="artists-droppable"
              isDropDisabled={false}
              isCombineEnabled={false}
              ignoreContainerClipping={false}
              direction={isHorizontal ? "horizontal" : "vertical"}
            >
              {(provided) => (
                <Box ref={provided.innerRef} {...provided.droppableProps}>
                  {values.artists?.map((artist, index) => (
                    <Draggable key={artist?.id} draggableId={artist?.id.toString()} index={index}>
                      {(providedDrag) => (
                        <div
                          ref={providedDrag.innerRef}
                          {...providedDrag.draggableProps}
                          {...providedDrag.dragHandleProps}
                          style={{ ...providedDrag.draggableProps.style }}
                        >
                          <ArtistItem
                            key={artist.id}
                            name={artist.name}
                            label={artist.title}
                            checked={
                              primaryArtistId != null
                                ? String((artist as any)?.id) === String(primaryArtistId)
                                : false
                            }
                            role={artist.role}
                            onChange={async () => {
                              const selectedId = artist?.id;
                              if (!selectedId) return;

                              // Նոր artists լիստը՝ Main / Feat կարգավորումով
                              const prevArtists = values.artists || [];
                              const updatedArtists = prevArtists.map(a =>
                                a.id === selectedId ? { ...a, role: "MAIN" } : { ...a, role: "FEAT" }
                              );

                              // Նախ form-ում փոփոխությունը կատարենք
                              change("artists", updatedArtists);

                              // Հետո API-ին ուղարկենք
                              try {
                                await upDateRole(Number(trackId), Number(selectedId), "MAIN");
                                console.log(`Artist ${selectedId} updated to MAIN`);

                                // Demote any other MAIN on the server (backend doesn't always auto-demote)
                                const toDemote = prevArtists
                                  .filter((a: any) => a?.id && a.id !== selectedId && a.role === "MAIN");
                                await Promise.all(
                                  toDemote.map((a: any) => upDateRole(Number(trackId), Number(a.id), "FEAT"))
                                );
                              } catch (err) {
                                console.error("Failed to update artist role:", err);
                                // Եթե պետք է form-ը վերադարձնել նախկին վիճակին
                                change("artists", prevArtists);
                              }
                            }}
                            onRemove={() => {
                              const filteredArtists = (values.artists || []).filter(a => a.id !== artist.id);
                              change("artists", filteredArtists);
                            }}
                          />

                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext> : <ArtistsBox><span>No added artists</span></ArtistsBox>}
        </FormListControl>


        <FormInput
          id="track-title"
          name="title"
          label="Track title"
          placeholder="Enter Track Title"
          initialValue={values?.title || ''}
          tooltip="For Singles, the track title must match the release title and include the track name plus its version (if any).
If the track is a special version (e.g., Afrojack, David Guetta – Hero (Nicky Romero Remix)), enter Nicky Romero Remixas the Track Version—without brackets.
"
          required
        />

        <FormInput
          name="version"
          label="Track version"
          placeholder="Enter Track Version"
          initialValue={values?.version || ''}
          tooltip="The track version appears after the track title on DSPs and helps distinguish it from the original.
          This field is shown without brackets.
          You may enter your own version or use standard ones like Extended Mix, Special Long Remix, Radio Mix, Remix, Mashup, etc.
          "
        />

        <FormDateBox>
          <FormSelect
            id="track-language"
            name="metadata_language"
            label="Track language"
            placeholder="Select Language"
            tooltip="Language must match the language spoken or sung in the audio."
            items={languages.map(lang => ({
              label: lang.language,
              value: lang.code,
            }))}
            filter
            required
          />

          <FormDateOnlyYear
            name="recording_year"
            label="Recording Year"
            initialValue={Number(recordingYearDate)}
          />

        </FormDateBox>

        <FormListControl
          id="contributors"
          name="contributors"
          label="Contributors"
          noItemsLabel="No added contributors"
          addItemLabel="Create New Contributors"
          onAddItem={() => setIsOpenContributorsModal(true)}
          tooltip="tooltip"
          totalItems={values?.contributors?.length}
        >

          {(values?.contributors || []).map(contributor => (
            <ContributorItem
              key={contributor.id}
              label={contributor?.name}
              setIsOpenContributorsModal={setIsOpenContributorsModal}
              description={
                [
                  contributor?.roles && contributor?.roles.map(role => role?.name).join(', '),
                  contributor?.instruments && contributor?.instruments.map(instrument => instrument?.name).join(', ')
                ]
                  .filter(Boolean)
                  .join(', ')
              }
              onRemove={() => {
                change(
                  'contributors',
                  (values?.contributors || []).filter(contr => contr.id !== contributor.id)
                );
              }}

            />
          ))}
        </FormListControl>


      </InfoInputsContainer>

      <AdditionalInfoContainer style={{ paddingTop: '20px' }}>
        <PrimaryGenreCol onClick={e => { e.stopPropagation(); setIsOpen(false); }}>
          <FormMultiSelectGaner
            isOpen={isOpenMain}
            setIsOpen={setIsOpenMain}
            name="main_genre"
            tooltip="Track genres"
            genres={genres as any}
            label={`Primary Genre`}
            selectionLimit={1}
            initialValue={stablePrimaryGenreInitial}
            Main={true}
            required
            placeholder="Select genres"


          />
        </PrimaryGenreCol>
        <AdditionalGenresCol onClick={e => { e.stopPropagation(); setIsOpenMain(false); }}>
          <FormMultiSelectGaner
            name="genre_ids"
            tooltip={null}
            genres={genres as any}
            label={`Additional Genres`}
            selectionLimit={2}
            initialValue={stableAdditionalGenresInitial}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            placeholder="Select additional genres"

          />
        </AdditionalGenresCol>
      </AdditionalInfoContainer>

      <ToggleContainer id="hasIsrc">
        <FormToggle
          name="hasIsrc"
          label="I have my own ISRC"
          tooltip="If this track is being published for the first time, leave the field empty — we will assign a new ISRC.
If the track was previously released, enter its existing ISRC to retain streaming history.
Unlike EAN/UPC, an ISRC can be reused for the same track across different releases — but only if the audio and metadata are identical.
⚠️ Provide the correct code. After submission, it cannot be changed; corrections require a takedown.
Example format: AABBB2200000.
"
          initialValue={!!values.isrc}
        />
      </ToggleContainer>

      {values.hasIsrc && (
        <EANUPCContainer>
          <FormInput
            name="isrc"
            label="ISRC"
            tooltip="I have my own ISRC
If this track is being published for the first time, leave the field empty — we will assign a new ISRC.
If the track was previously released, enter its existing ISRC to retain streaming history.
Unlike EAN/UPC, an ISRC can be reused for the same track across different releases — but only if the audio and metadata are identical.
⚠️ Provide the correct code. After submission, it cannot be changed; corrections require a takedown.
Example format: AABBB2200000."
            initialValue={values?.isrc ?? ''}
          />
        </EANUPCContainer>
      )}

      <FormInputBox>
        <FormInput
          id="track-lyrics"
          name="lyrics"
          label="Track lyrics"
          placeholder="Enter Track Lyrics"
          tooltip="tooltip"
          initialValue={values?.lyrics || ''}
          multiline
          required
        />

      </FormInputBox>

      <AddArtistModal
        trackId={trackId}
        isOpen={isOpenArtistsModal}
        onHide={() => setIsOpenArtistsModal(false)}
        artists={[
          ...artists.filter(artist => artist?.id != null)
            .filter(artist => !artistData.find(a => a?.id === artist?.id))
        ]}
        createArtist={createArtist}
        artistCreating={artistCreating}
        selectedArtist={selectedArtist}
        setSelectedArtist={setSelectedArtist}
      />

      <AddContributorModal
        name=""
        isOpen={isOpenContributorsModal}
        onHide={() => setIsOpenContributorsModal(false)}
        roles={roles}
        instruments={instruments}
        contributors={contributors}
        createContributor={createContributor}
        contributorCreating={contributorCreating}
      />
    </Container>
  );
};

const Container = styled.div`width:100%;`;
const InfoInputsContainer = styled.div`
  width:100%;
  display:flex;
  flex-direction:column;
  gap:17px;
  margin:32px 0 0;
`;
const ToggleContainer = styled.div`margin:25px 0;`;
const AdditionalInfoContainer = styled.div`
  display:flex;
  gap:5px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 14px;
  }
`;
const EANUPCContainer = styled.div`margin-bottom:25px;`;
const FormInputBox = styled.div`
  margin-bottom:25px;
  display:flex;
  flex-direction:column;
  gap:8px;
`;
const InputBox = styled.div`
  display:flex;
  align-items:center;
  gap:7px;
  label {
    font-family: 'SF Pro Display', sans-serif;
    font-weight:500;
    font-size:13px;
    line-height:18px;
    vertical-align:middle;
  }
`;
const Box = styled.div`
  width:100%;
  display:flex;
  flex-wrap:wrap;
  gap:5px;
`;
const ArtistsBox = styled.div`
  width:100%;
  height:75px;
  border-radius:15px;
  background:#E9E9E9;
  display:flex;
  align-items:center;
  justify-content:center;
  span {
    font-family:SF Pro Display;
    font-weight:500;
    font-size:13px;
    line-height:18px;
    color:#00000080;
  }
`;
const FormDateBox = styled.div`
  width:100%;
  display:flex;
  gap:5px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 14px;
  }
`;

const PrimaryGenreCol = styled.div`
  width: 40%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const AdditionalGenresCol = styled.div`
  width: 60%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #6777FF;
  appearance: none;
  cursor: pointer;
  position: relative;
  background-color: ${(props) => (props.checked ? "#6777FF" : "#fff")};

  &:checked::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 6px;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
}`